import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import type { Locale } from '@lims/shared-types';
import { samplesApi } from '../../api/endpoints.js';
import type { LabelContent } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  Dialog,
  LoadingState,
  useToast,
} from '../../components/ui/index.js';
import { formatDate } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { encodeCode128, isEncodableAsCode128 } from './code128.js';
import { SampleTypeName } from './sample-type-fields.js';

/**
 * The label: preview, and print.
 *
 * ===========================================================================
 * PREVIEWING A LABEL IS NOT PRINTING ONE.
 * ===========================================================================
 *
 * Opening this dialog calls a GET that records NOTHING. The print count does
 * not move because somebody looked, and the audit trail does not claim a label
 * was produced. Pressing Print records the print FIRST and hands the page to
 * the browser's own dialog afterwards — so the count reflects an intention the
 * user expressed, and the honest limit of that is stated on the page: this
 * system cannot know whether paper came out of a printer (CLAUDE.md section 8).
 *
 * A REPRINT MINTS NOTHING. The sample keeps its number; the print count
 * advances. A second label for the same sample is the same identity printed
 * twice, which is the whole reason the count exists.
 *
 * THE BACKEND OWNS THE CONTENT; THIS OWNS THE LAYOUT (decision D69). The API
 * returns fields, a scan value and a payload — never a layout and never a
 * printer command, because committing to one printer's language would commit
 * the product to one laboratory's hardware. The preview is rendered at the
 * laboratory's OWN configured millimetre size, read from the same response, so
 * what is on screen is the physical object.
 */
export function SampleLabelDialog({
  sampleId,
  pieceId,
  isOpen,
  onClose,
  mayPrint = true,
}: {
  readonly sampleId: string;
  /** Set for a piece label; absent for the sample's own. */
  readonly pieceId?: string | undefined;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  /** False for a voided sample (D211): its label can be seen, not printed. */
  readonly mayPrint?: boolean;
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const locale = currentLocale();

  const label = useQuery({
    queryKey: ['sample', sampleId, 'label', pieceId ?? null, locale],
    queryFn: () =>
      samplesApi.label(sampleId, {
        ...(pieceId ? { pieceId } : {}),
        locale,
      }),
    enabled: isOpen,
  });

  /**
   * Records the print, then opens the browser's dialog.
   *
   * In that order deliberately: the record is of a person deciding to print,
   * which is a fact this system has. Whether the printer then produced paper is
   * a fact it does not have, and the notice below says so rather than the count
   * implying it.
   */
  const print = useMutation({
    mutationFn: () =>
      samplesApi.recordPrint(sampleId, {
        ...(pieceId ? { pieceId } : {}),
        locale,
      }),
    onSuccess: async (recorded) => {
      await queryClient.invalidateQueries({
        queryKey: ['sample', sampleId, 'label'],
      });
      show({
        tone: 'success',
        message: t('samples.printRecorded', { count: recorded.printCount }),
      });
      window.print();
    },
  });

  // While the preview is open, print media shows the label alone. Removed on
  // close so an ordinary page still prints as an ordinary page.
  useEffect(() => {
    if (!isOpen) return;
    document.body.classList.add('printing-label');
    return () => document.body.classList.remove('printing-label');
  }, [isOpen]);

  const content = label.data;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('samples.labelTitle')}
      size="lg"
      footer={
        <ActionBar>
          <ActionButton priority="tertiary" onPress={onClose}>
            {t('common.close')}
          </ActionButton>
          {can(PERMISSIONS.LABELS_PRINT) && mayPrint ? (
            <ActionButton
              priority="primary"
              isDisabled={!content}
              isLoading={print.isPending}
              onPress={() => print.mutate()}
            >
              {content && content.printCount > 0
                ? t('samples.reprintLabel')
                : t('samples.printLabel')}
            </ActionButton>
          ) : null}
        </ActionBar>
      }
    >
      {label.isLoading ? (
        <LoadingState label={t('common.loading')} />
      ) : label.error || !content ? (
        <Alert tone="danger" title={t('samples.labelUnavailable')} />
      ) : (
        <div className="flex flex-col gap-stack">
          {/*
            The page size is the laboratory's configured stock, written from the
            response. Not a constant in this file, and not in the stylesheet.
          */}
          <style>{`@page { size: ${Number(content.widthMm)}mm ${Number(
            content.heightMm,
          )}mm; margin: 0; }`}</style>

          <LabelSheet content={content} locale={locale} />

          <p className="text-caption text-default-600">
            {t('samples.labelSizeNotice', {
              width: Number(content.widthMm),
              height: Number(content.heightMm),
            })}
          </p>

          {/* Previewing is not printing, and printing is not proof of paper. */}
          <Alert
            tone="info"
            title={t('samples.printHonestyTitle')}
            description={t('samples.printHonestyBody')}
          />

          <p className="text-caption text-default-600">
            {content.printCount > 0
              ? t('samples.printedTimes', { count: content.printCount })
              : t('samples.neverPrinted')}
          </p>

          {print.error ? (
            <Alert tone="danger" title={t('samples.printFailed')} />
          ) : null}
        </div>
      )}
    </Dialog>
  );
}

/**
 * The label itself, at its real physical size.
 *
 * Everything is in millimetres, from the tenant's settings, so the preview and
 * the print are the same object. Nothing about the arrangement is a scientific
 * claim: it is an identifier, a type, a date and a barcode.
 */
function LabelSheet({
  content,
  locale,
}: {
  readonly content: LabelContent;
  readonly locale: Locale;
}) {
  const widthMm = Number(content.widthMm);
  const heightMm = Number(content.heightMm);

  return (
    <div
      data-print-target="label"
      // Always left-to-right. A label is read by a scanner and by somebody
      // holding a sample, and the identifier's digits must not reorder.
      dir="ltr"
      className="mx-auto flex max-w-full flex-col justify-between overflow-hidden border border-default-300 bg-white p-2 text-black"
      style={{ width: `${widthMm}mm`, height: `${heightMm}mm` }}
    >
      <div className="min-w-0">
        {/* The laboratory's own configured lines. None is hardcoded. */}
        {content.extraLines.map((line) => (
          <p key={line} className="truncate text-[2.6mm] leading-tight">
            {line}
          </p>
        ))}
      </div>

      <div className="min-w-0">
        <p className="truncate font-mono text-[4mm] font-bold leading-none">
          {content.sampleNumber}
        </p>
        {content.pieceCode ? (
          <p className="truncate font-mono text-[3mm] leading-tight">
            {content.pieceCode}
          </p>
        ) : null}
        <p className="truncate text-[2.6mm] leading-tight">
          <SampleTypeName code={content.sampleType} />
          {content.jobNumber ? ` · ${content.jobNumber}` : ''}
        </p>
        <p className="truncate text-[2.6mm] leading-tight">
          {formatDate(content.receivedAt, { locale })}
        </p>
      </div>

      <LabelSymbol content={content} />
    </div>
  );
}

/**
 * The machine-readable mark.
 *
 * Code 128 is drawn from the scan value, which is exactly the Sample ID or the
 * piece code — nothing else, because a scanner is a keyboard and what it types
 * goes into the register's search box (decision D68).
 *
 * QR IS NOT DRAWN YET, AND SAYS SO. A laboratory that has configured QR gets a
 * plain statement that this build cannot produce the image, not a blank space
 * and not a Code 128 contradicting the setting it chose. The payload is data
 * only — never a URL and never a token — and is shown as text so the setting is
 * at least inspectable.
 */
function LabelSymbol({ content }: { readonly content: LabelContent }) {
  const { t } = useTranslation();

  if (content.symbology !== 'code128') {
    return (
      <p className="text-[2.4mm] leading-tight">
        {t('samples.symbologyNotRenderable', { symbology: content.symbology })}
      </p>
    );
  }

  if (!isEncodableAsCode128(content.scanValue)) {
    // Refused rather than approximated: a label missing a character scans as a
    // different sample.
    return (
      <p className="text-[2.4mm] leading-tight">
        {t('samples.scanValueNotEncodable')}
      </p>
    );
  }

  const symbol = encodeCode128(content.scanValue);

  return (
    <svg
      // The symbol scales to the label's width; the viewBox is in MODULES, so
      // the bars stay in exact proportion at any physical size.
      viewBox={`0 0 ${symbol.totalModules} 30`}
      preserveAspectRatio="none"
      className="h-[10mm] w-full"
      role="img"
      aria-label={content.scanValue}
    >
      {symbol.bars.map((bar) => (
        <rect
          key={`${bar.x}-${bar.width}`}
          x={bar.x}
          y={0}
          width={bar.width}
          height={30}
          fill="#000"
        />
      ))}
    </svg>
  );
}
